/**
 * useApiRequest - React Hook for API requests with automatic cancellation
 *
 * Features:
 * 1. Auto-manage request lifecycle
 * 2. Auto-cancel on component unmount
 * 3. Built-in loading/error/data state management
 * 4. Support manual retry
 * 5. Support debounce
 * 6. Prevent race conditions
 *
 * Architecture:
 * - useApiRequest: Handles React-level state and lifecycle management
 * - useAxios: Handles HTTP communication and AbortController management
 * - RequestManager: Centrally manages all AbortController instances
 * - RequestContext: Passes requestKey through call chain
 *
 * @example
 * // Basic usage
 * const { data, loading, error, execute } = useApiRequest(
 *   () => fetchQuestionnaires(),
 *   { immediate: true }
 * );
 *
 * @example
 * // Race condition prevention (search scenario)
 * const { execute } = useApiRequest(
 *   (keyword: string) => searchQuestionnaires(keyword),
 *   {
 *     requestKey: 'questionnaire-search',  // Old requests auto-canceled
 *     debounce: 300,                       // 300ms debounce
 *   }
 * );
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { RequestContext, requestManager } from '@/utils/RequestManager';

/**
 * API request state
 */
export interface ApiRequestState<T> {
  /** Request response data */
  data: T | null;
  /** Loading indicator */
  loading: boolean;
  /** Error (if any) */
  error: Error | null;
}

/**
 * useApiRequest configuration options
 */
export interface UseApiRequestOptions<T> {
  /** Execute immediately on hook initialization (default: false) */
  immediate?: boolean;

  /** Initial data (default: null) */
  initialData?: T | null;

  /** Unique request identifier for race prevention and cancellation (optional) */
  requestKey?: string;

  /** Debounce delay in milliseconds, 0 means no debounce (default: 0) */
  debounce?: number;

  /** Success callback */
  onSuccess?: (data: T) => void;

  /** Error callback */
  onError?: (error: Error) => void;

  /** Finally callback (called regardless of success/error) */
  onFinally?: () => void;
}

/**
 * useApiRequest return value
 */
export interface UseApiRequestReturn<T, Args extends unknown[]>
  extends ApiRequestState<T> {
  /** Execute the request */
  execute: (...args: Args) => Promise<T | undefined>;

  /** Manually cancel current request */
  cancel: (reason?: string) => void;

  /** Reset state to initial values */
  reset: () => void;
}

/**
 * Check if error is a cancellation error
 */
const isCancelError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const err = error as {
    name?: string;
    code?: string;
    message?: string;
  };

  return (
    err.name === 'AbortError' ||
    err.name === 'CanceledError' ||
    err.code === 'ERR_CANCELED' ||
    err.message === 'canceled'
  );
};

/**
 * useApiRequest Hook
 *
 * @param apiFunction API function
 * @param options Configuration options
 * @returns API request state and control functions
 */
export const useApiRequest = <T = unknown, Args extends unknown[] = unknown[]>(
  apiFunction: (...args: Args) => Promise<T>,
  options: UseApiRequestOptions<T> = {},
): UseApiRequestReturn<T, Args> => {
  const {
    immediate = false,
    initialData = null,
    requestKey,
    debounce = 0,
    onSuccess,
    onError,
    onFinally,
  } = options;

  // State management
  const [state, setState] = useState<ApiRequestState<T>>({
    data: initialData,
    loading: false,
    error: null,
  });

  // Refs
  const mountedRef = useRef(true);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const executingCountRef = useRef(0); // Track number of executing requests

  // Use "latest ref" pattern for callbacks to ensure stable executeRequest identity
  // while always calling the most recent callback version
  const callbacksRef = useRef({ onSuccess, onError, onFinally });
  // Always keep ref updated with latest callbacks (no useEffect needed)
  callbacksRef.current = { onSuccess, onError, onFinally };

  /**
   * Auto-cancel on component unmount
   */
  useEffect(() => {
    // Set mounted to true on mount (important for StrictMode re-mounts)
    mountedRef.current = true;

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // If requestKey exists, cancel through RequestManager
      // useAxios already handles controller creation and management
      if (requestKey) {
        requestManager.cancel(requestKey, 'Component unmounted');
      }
    };
  }, [requestKey]);

  /**
   * Cancel current request
   * Cancel through RequestManager to ensure actual request is canceled
   */
  const cancel = useCallback(
    (reason?: string) => {
      if (requestKey) {
        requestManager.cancel(requestKey, reason);
      }
    },
    [requestKey],
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    // Reset execution count
    executingCountRef.current = 0;

    // Reset state
    setState({
      data: initialData,
      loading: false,
      error: null,
    });
  }, [initialData]);

  /**
   * Core API request execution logic
   *
   * - No longer manages AbortController (handled by useAxios)
   * - Only handles React-level state management and lifecycle
   * - Passes requestKey to useAxios through RequestContext
   */
  const executeRequest = useCallback(
    async (...args: Args): Promise<T | undefined> => {
      // Increment execution count
      executingCountRef.current += 1;

      // Set loading state (if any request is executing)
      if (mountedRef.current && executingCountRef.current > 0) {
        setState((prev) => ({ ...prev, loading: true, error: null }));
      }

      try {
        // Execute API request in requestKey context
        // useAxios will get requestKey from RequestContext.getKey()
        // and automatically manage AbortController creation, registration, and cancellation
        const result = await RequestContext.run(requestKey ?? null, () =>
          apiFunction(...args),
        );

        // Check if component is unmounted
        if (!mountedRef.current) {
          executingCountRef.current = Math.max(
            0,
            executingCountRef.current - 1,
          );
          return undefined;
        }

        // Decrement execution count
        executingCountRef.current = Math.max(0, executingCountRef.current - 1);

        // Only set loading = false when no requests are executing
        setState({
          data: result,
          loading: executingCountRef.current > 0,
          error: null,
        });

        // Call success callback using latest ref
        callbacksRef.current.onSuccess?.(result);

        return result;
      } catch (err: unknown) {
        // Decrement execution count
        executingCountRef.current = Math.max(0, executingCountRef.current - 1);

        // Check if it's a cancel error
        if (isCancelError(err)) {
          // Request was canceled, only set loading = false if no other requests are executing
          if (mountedRef.current) {
            setState((prev) => ({
              ...prev,
              loading: executingCountRef.current > 0,
            }));
          }
          return undefined;
        }

        // Check if component is unmounted
        if (!mountedRef.current) {
          return undefined;
        }

        // Update error state
        const error = err instanceof Error ? err : new Error(String(err));
        setState((prev) => ({
          ...prev,
          loading: executingCountRef.current > 0,
          error,
        }));

        // Call error callback using latest ref
        callbacksRef.current.onError?.(error);

        throw error;
      } finally {
        // Call finally callback using latest ref
        callbacksRef.current.onFinally?.();
      }
    },
    [apiFunction, requestKey],
  );

  /**
   * Execute request (with debounce support)
   */
  const execute = useCallback(
    (...args: Args): Promise<T | undefined> => {
      // If debounce is set, use debounce logic
      if (debounce > 0) {
        return new Promise((resolve) => {
          // Clear previous timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          // Set new timer
          debounceTimerRef.current = setTimeout(() => {
            executeRequest(...args).then(resolve);
          }, debounce);
        });
      }

      // Execute request immediately
      return executeRequest(...args);
    },
    [debounce, executeRequest],
  );

  /**
   * If immediate is true, execute on hook initialization
   * Note: immediate mode assumes API function doesn't need arguments
   */
  useEffect(() => {
    if (immediate) {
      // Type assertion, immediate mode assumes API function needs no arguments
      execute(...([] as unknown as Args));
    }
  }, [immediate]);

  return {
    ...state,
    execute,
    cancel,
    reset,
  };
};

export default useApiRequest;
